import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { MatStepper } from '@angular/material/stepper';
import { environment } from 'src/environments/environment';

type PageState = 'loading' | 'invalid' | 'stepper' | 'submitting' | 'success';

interface InvitationDetails {
  email: string;
  facilityId: string | null;
  facilityName: string;
  hubName?: string;
}

const ALL_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Puerto_Rico',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Bogota',
  'America/Lima',
  'America/Santiago',
  'America/Sao_Paulo',
  'America/Buenos_Aires',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Helsinki',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Vienna',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Colombo',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
  'Pacific/Fiji',
];

@Component({
  selector: 'app-join-page',
  templateUrl: './join-page.component.html',
  styleUrls: ['./join-page.component.scss'],
  standalone: false
})
export class JoinPageComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper!: MatStepper;

  private hadDarkTheme = false;

  state: PageState = 'loading';
  invitation: InvitationDetails | null = null;
  invalidMessage = '';
  errorMessage = '';

  accountForm!: UntypedFormGroup;
  facilityForm!: UntypedFormGroup;
  securityForm!: UntypedFormGroup;

  hidePassword = true;
  hideConfirm = true;
  filteredTimezones: string[] = ALL_TIMEZONES;

  private token = '';
  private readonly cfUrl = `${environment.constants.cloudfunctionsURL}invitations-join`;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private http: HttpClient,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  ngOnInit(): void {
    this.hadDarkTheme = this.doc.body.classList.contains('dark-theme');
    this.doc.body.classList.remove('dark-theme');

    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    if (!this.token) {
      this.invalidMessage = 'No invitation token found in this link. Please check your invitation email.';
      this.state = 'invalid';
      return;
    }
    this.validateToken();
  }

  ngOnDestroy(): void {
    if (this.hadDarkTheme) {
      this.doc.body.classList.add('dark-theme');
    }
  }

  private async validateToken(): Promise<void> {
    try {
      const params = new HttpParams().set('token', this.token);
      this.invitation = await firstValueFrom(
        this.http.get<InvitationDetails>(this.cfUrl, { params })
      );
      this.buildForms();
      this.state = 'stepper';
    } catch (err: any) {
      this.invalidMessage = err?.status === 404
        ? 'This invitation link is invalid or has already been used. Please request a new invitation.'
        : 'Could not verify your invitation. Please try again later.';
      this.state = 'invalid';
    }
  }

  private buildForms(): void {
    this.accountForm = this.fb.group(
      {
        name: ['', [Validators.required, Validators.minLength(2)]],
        cellPhone: ['', [Validators.required]],
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: JoinPageComponent.passwordsMatch }
    );

    this.facilityForm = this.fb.group({
      facilityName: [this.invitation?.facilityName || '', [Validators.required]],
      emailDomain: ['', [Validators.required, JoinPageComponent.domainValidator]],
      timeZone: ['', [Validators.required]],
      contactEmail: ['', [Validators.required, Validators.email]],
      facilityPhone: ['', [Validators.required]],
      facilityAddress: ['', [Validators.required]],
      facilityCity: ['', [Validators.required]],
      facilityState: ['', [Validators.required, Validators.maxLength(2)]],
      facilityZip: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      specialty: [''],
      pacsUrl: [''],
    });

    this.securityForm = this.fb.group({
      accepted: [false, [Validators.requiredTrue]],
    });

    this.facilityForm.get('timeZone')!.valueChanges.subscribe((val: string) => {
      const q = (val || '').toLowerCase();
      this.filteredTimezones = ALL_TIMEZONES.filter(tz => tz.toLowerCase().includes(q));
    });
  }

  static passwordsMatch(g: AbstractControl): ValidationErrors | null {
    const pw = g.get('password')?.value;
    const cp = g.get('confirmPassword')?.value;
    return pw && cp && pw !== cp ? { passwordMismatch: true } : null;
  }

  static domainValidator(ctrl: AbstractControl): ValidationErrors | null {
    const v: string = (ctrl.value || '').trim();
    if (!v) return null;
    if (v.includes('@')) return { noAt: true };
    if (!/^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/.test(v)) return { invalidDomain: true };
    return null;
  }

  async onComplete(): Promise<void> {
    if (this.securityForm.invalid) { this.securityForm.markAllAsTouched(); return; }
    this.state = 'submitting';
    this.errorMessage = '';

    const a = this.accountForm.value;
    const f = this.facilityForm.value;

    try {
      await firstValueFrom(
        this.http.post(this.cfUrl, {
          token: this.token,
          name: (a.name as string).trim(),
          phone: (a.cellPhone as string).replace(/\D/g, ''),
          password: a.password,
          facility: {
            name: (f.facilityName as string).trim(),
            emailDomain: (f.emailDomain as string).trim().toLowerCase(),
            timeZone: f.timeZone,
            contactEmail: (f.contactEmail as string).trim().toLowerCase(),
            phone: (f.facilityPhone as string).replace(/\D/g, ''),
            address: (f.facilityAddress as string).trim(),
            city: (f.facilityCity as string).trim(),
            state: (f.facilityState as string).trim().toUpperCase(),
            zip: (f.facilityZip as string).trim(),
            specialty: (f.specialty as string)?.trim() || '',
            pacsUrl: (f.pacsUrl as string)?.trim() || '',
          },
        })
      );
      this.state = 'success';
    } catch (err: any) {
      this.errorMessage = err?.error?.error || err?.message || 'An unexpected error occurred. Please try again.';
      this.state = 'stepper';
    }
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }
}
